import type { LookImagePayload } from "../../../features/ai/lookMatchApi";

/**
 * Downscale an image for the vision model: long edge ≤ 1568 px (the size the
 * model works at — anything larger is resized server-side anyway and only costs
 * upload time and tokens), re-encoded as JPEG. A phone photo goes from ~4 MB to
 * ~200 KB, which also keeps the request far below the 5 MB per-image limit.
 */
export const MAX_EDGE = 1568;

export interface PreparedImage extends LookImagePayload {
  /** Object URL for the thumbnail; revoke when the image is removed. */
  previewUrl: string;
  width: number;
  height: number;
}

export async function prepareImage(src: Blob, maxEdge = MAX_EDGE, quality = 0.86): Promise<PreparedImage> {
  if (!src.type.startsWith("image/")) throw new Error("That file is not an image.");
  const bmp = await createImageBitmap(src);
  const scale = Math.min(1, maxEdge / Math.max(bmp.width, bmp.height));
  const w = Math.max(1, Math.round(bmp.width * scale));
  const h = Math.max(1, Math.round(bmp.height * scale));
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Could not read that image.");
  // JPEG has no alpha: paint white first so a transparent PNG render does not
  // turn black — the model would read that as a black backdrop.
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, w, h);
  ctx.drawImage(bmp, 0, 0, w, h);
  bmp.close();
  const blob = await new Promise<Blob>((res, rej) =>
    canvas.toBlob((b) => (b ? res(b) : rej(new Error("Could not encode the image."))), "image/jpeg", quality),
  );
  return { mediaType: "image/jpeg", data: await toBase64(blob), previewUrl: URL.createObjectURL(blob), width: w, height: h };
}

async function toBase64(blob: Blob): Promise<string> {
  const buf = new Uint8Array(await blob.arrayBuffer());
  let s = "";
  const CHUNK = 0x8000;
  for (let i = 0; i < buf.length; i += CHUNK) s += String.fromCharCode(...buf.subarray(i, i + CHUNK));
  return btoa(s);
}
